import { useEffect } from "react";

const BASE_URL = "https://livius-training.co.uk";

const EUSR_COURSES = [
  {
    name: "EUSR National Water Hygiene (Blue Card)",
    description: "The EUSR National Water Hygiene scheme — widely known as the Blue Card — is the essential health and hygiene qualification for anyone working on the UK's potable water network. Mandatory for all major UK water companies.",
    url: `${BASE_URL}/NationalWaterHygiene`,
    duration: "1 day",
    price: "150",
    credential: "EUSR National Water Hygiene Registration (Blue Card)",
    validFor: "5 years"
  },
  {
    name: "EUSR SHEA Water (Safety, Health & Environmental Awareness)",
    description: "EUSR-accredited SHEA Water is the mandatory induction qualification for all new entrants to the UK clean water industry, covering core safety, health, and environmental awareness before commencing site work.",
    url: `${BASE_URL}/SHEAWater`,
    duration: "1 day",
    price: "150",
    credential: "EUSR SHEA Water Registration",
    validFor: "Life of registration"
  },
  {
    name: "EUSR Products for Drinking Water",
    description: "EUSR-accredited course covering drinking water products compliance for water industry operatives working with products that contact potable water supplies.",
    url: `${BASE_URL}/NationalWaterHygiene`,
    duration: "1 day",
    price: "150",
    credential: "EUSR Products for Drinking Water Registration",
    validFor: "3 years"
  }
];

const EUSR_FAQS = [
  {
    question: "What are EUSR courses?",
    answer: "EUSR (Energy & Utility Skills Register) courses are the industry-standard qualifications for anyone working in the UK water, gas, power, and waste management sectors. They are administered by Energy & Utility Skills and are mandatory for site access at all major UK water companies."
  },
  {
    question: "Which EUSR courses does Livius Training offer?",
    answer: "Livius Training offers three EUSR-accredited courses: National Water Hygiene (Blue Card), SHEA Water (Safety, Health & Environmental Awareness), and EUSR Products for Drinking Water. All are delivered as one-day courses at our Yorkshire training centre or onsite UK-wide."
  },
  {
    question: "Is the EUSR Blue Card mandatory in the UK?",
    answer: "Yes. The EUSR National Water Hygiene Blue Card is mandatory under the Water Supply (Water Quality) Regulations 2016 for any operative accessing potable water assets. All major UK water companies — including Yorkshire Water, Anglian Water, Severn Trent, United Utilities, Thames Water, and Northumbrian Water — require a valid Blue Card before granting site access."
  },
  {
    question: "How long does EUSR registration last?",
    answer: "The National Water Hygiene Blue Card is valid for 5 years. SHEA Water registration is valid for life. EUSR Products for Drinking Water registration is valid for 3 years. All must be renewed before expiry to maintain site access."
  },
  {
    question: "Can EUSR courses be delivered at our premises?",
    answer: "Yes. Livius Training delivers all EUSR courses onsite at client premises across the UK for groups of 6 or more delegates. We also run regular open courses at our training centre in Boroughbridge, North Yorkshire."
  },
  {
    question: "How much do EUSR courses cost?",
    answer: "EUSR courses at Livius Training are £150 + VAT per delegate for open courses at our Yorkshire centre. Group discounts are available for onsite delivery. Contact us on 01423 396780 for group booking rates."
  },
  {
    question: "What happens if you work on water sites without an EUSR Blue Card?",
    answer: "Working on potable water sites without a valid EUSR Blue Card risks immediate site removal, contract termination, and potential prosecution under the Water Supply (Water Quality) Regulations 2016. Water companies conduct random spot checks on operatives."
  },
  {
    question: "Do you offer weekend or shift-friendly EUSR training?",
    answer: "Yes. We offer flexible scheduling including weekend sessions and out-of-hours training for shift workers. Contact us to arrange EUSR training that fits your operational schedule."
  }
];

export default function EUSRStructuredData() {
  useEffect(() => {
    // ItemList of EUSR courses
    const itemListSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "EUSR Courses UK — Livius Training",
      "description": "EUSR-accredited water industry training courses delivered UK-wide from our Yorkshire training centre.",
      "itemListElement": EUSR_COURSES.map((course, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "item": {
          "@type": "Course",
          "name": course.name,
          "description": course.description,
          "url": course.url,
          "provider": {
            "@type": "Organization",
            "name": "Livius Training",
            "url": BASE_URL
          },
          "educationalCredentialAwarded": course.credential,
          "timeRequired": course.duration,
          "inLanguage": "en-GB",
          "offers": {
            "@type": "Offer",
            "price": course.price,
            "priceCurrency": "GBP",
            "availability": "https://schema.org/InStock",
            "url": course.url
          }
        }
      }))
    };

    // FAQPage schema with EUSR-citeable answers
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": EUSR_FAQS.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    // EducationalOrganization schema
    const eduSchema = {
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      "name": "Livius Training",
      "description": "EUSR-accredited training provider delivering water industry qualifications UK-wide from our Yorkshire training centre.",
      "url": BASE_URL,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Rabbit Hill Business Park, Great North Road",
        "addressLocality": "Boroughbridge",
        "addressRegion": "North Yorkshire",
        "postalCode": "HG5 0FF",
        "addressCountry": "GB"
      },
      "telephone": "01423396780",
      "areaServed": ["United Kingdom", "Yorkshire", "North England"],
      "hasCourse": EUSR_COURSES.map(c => ({
        "@type": "Course",
        "name": c.name,
        "url": c.url
      }))
    };

    const scripts = [];

    [itemListSchema, faqSchema, eduSchema].forEach((schema, i) => {
      const id = `eusr-structured-data-${i}`;
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const script = document.createElement("script");
      script.id = id;
      script.type = "application/ld+json";
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      scripts.push(id);
    });

    return () => {
      scripts.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, []);

  return null;
}